<template>
  <div>
    <v-dialog :value="value && step !== 'link'" max-width="560px" scrollable @input="onDialogInput">
      <v-card v-if="candidate">
        <v-card-title>
          Confirmar contacto
          <v-spacer />
          <v-btn icon aria-label="Cerrar" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="companyName" class="pt-1">{{ companyName }}</v-card-subtitle>

        <v-card-text>
          <template v-if="step === 'company'">
            <p class="mb-2">¿En qué empresa trabaja?</p>
            <v-radio-group v-model="chosenCompanyId" class="mt-0" hide-details>
              <v-radio
                v-for="option in possibleCompanies"
                :key="option.companyId"
                :value="option.companyId"
                :label="companyLabel(option)"
              />
            </v-radio-group>
          </template>

          <template v-else-if="matches.length">
            <p class="mb-2">Ya hay alguien parecido en Pipedrive. ¿Es la misma persona?</p>
            <v-list dense>
              <v-list-item v-for="match in matches" :key="match.id">
                <v-list-item-content>
                  <v-list-item-title>{{ match.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="match.orgName">{{ match.orgName }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn small color="primary" depressed :disabled="saving" @click="save({ existingPipedrivePersonId: match.id })">
                    Usar esta persona
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </template>

          <div v-else-if="loadingOrg" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <v-form v-else ref="form" @submit.prevent="save()">
            <v-text-field
              v-model.trim="form.name"
              label="Nombre"
              :rules="[v => !!v || 'Escribe el nombre']"
              :disabled="saving"
              outlined
              dense
            />
            <v-text-field v-model.trim="form.title" label="Cargo" :disabled="saving" outlined dense />
            <v-text-field v-model.trim="form.email" label="Email" type="email" :disabled="saving" outlined dense />
            <v-text-field v-model.trim="form.linkedinUrl" label="LinkedIn" :disabled="saving" outlined dense />
            <org-chart-placement-fields
              v-model="placement"
              :people="managerOptions"
              :areas="orgAreas"
              :disabled="saving"
            />
          </v-form>

          <v-alert v-if="errorText" type="error" dense text class="mt-3 mb-0">{{ errorText }}</v-alert>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <template v-if="step === 'company'">
            <v-btn text @click="close">Cancelar</v-btn>
            <v-btn color="primary" depressed :disabled="!chosenCompanyId" @click="chooseCompany(chosenCompany)">Seguir</v-btn>
          </template>
          <template v-else-if="matches.length">
            <v-btn text :disabled="saving" @click="matches = []">Volver</v-btn>
            <v-btn text :loading="saving" @click="save({ skipDuplicateCheck: true })">Crear de todas formas</v-btn>
          </template>
          <template v-else>
            <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
            <v-btn color="primary" depressed :loading="saving" :disabled="loadingOrg" @click="save()">Confirmar</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <link-company-modal
      :value="linkOpen"
      :company="linkCompany"
      @input="onLinkInput"
      @linked="onLinked"
    />
  </div>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import LinkCompanyModal from './LinkCompanyModal.vue';
import OrgChartPlacementFields from './OrgChartPlacementFields.vue';
import { errorMessage, toPlacement } from '../utils/org-chart.utils';

// Confirma un candidato: lo crea en Pipedrive (o usa una persona existente) y lo ubica.
// Si la empresa del candidato no está en Pipedrive, primero pasa por LinkCompanyModal.
export default {
  name: 'ConfirmCandidateModal',
  components: { LinkCompanyModal, OrgChartPlacementFields },
  props: {
    value: { type: Boolean, default: false },
    candidate: { type: Object, default: null },
    // Organización de Pipedrive ya conocida (pestaña "Por revisar" de una empresa).
    pipedriveOrgId: { type: [Number, String], default: null },
    // Empresa elegida para un contacto sin empresa: { companyId, name, country, pipedriveOrgId }.
    company: { type: Object, default: null },
    // Árbol ya cargado. Si es null, el modal lo pide al backend.
    nodes: { type: Array, default: null },
    unplaced: { type: Array, default: () => [] },
    areas: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      step: 'form',
      resolvedOrgId: null,
      chosenCompanyId: null,
      selectedCompany: null,
      linkOpen: false,
      linkCompany: null,
      linked: false,
      loadingOrg: false,
      loadedNodes: [],
      loadedUnplaced: [],
      loadedAreas: {},
      form: { name: '', title: '', email: '', linkedinUrl: '' },
      placement: { manager: null, area: null },
      matches: [],
      saving: false,
      errorText: ''
    };
  },
  computed: {
    possibleCompanies() {
      return (this.candidate && this.candidate.possibleCompanies) || [];
    },
    chosenCompany() {
      return this.possibleCompanies.find(option => option.companyId === this.chosenCompanyId) || null;
    },
    companyName() {
      return this.selectedCompany ? this.selectedCompany.name : '';
    },
    orgNodes() {
      return this.nodes || this.loadedNodes;
    },
    orgUnplaced() {
      return this.nodes ? this.unplaced : this.loadedUnplaced;
    },
    orgAreas() {
      return this.nodes ? this.areas : this.loadedAreas;
    },
    managerOptions() {
      return [...this.orgNodes, ...this.orgUnplaced];
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.start();
      }
    }
  },
  methods: {
    companyLabel(option) {
      return option.country ? `${option.name} (${option.country})` : option.name;
    },
    start() {
      const candidate = this.candidate || {};
      this.form = {
        name: candidate.fullName || '',
        title: candidate.title || '',
        email: candidate.email || '',
        linkedinUrl: candidate.linkedinUrl || ''
      };
      this.placement = { manager: null, area: null };
      this.matches = [];
      this.errorText = '';
      this.saving = false;
      this.linked = false;
      this.linkOpen = false;
      this.resolvedOrgId = null;
      this.selectedCompany = this.company;
      this.chosenCompanyId = this.company ? this.company.companyId : null;

      if (this.pipedriveOrgId) {
        this.resolvedOrgId = this.pipedriveOrgId;
        this.goToForm();
        return;
      }
      const company = this.company || (this.possibleCompanies.length === 1 ? this.possibleCompanies[0] : null);
      if (company) this.chooseCompany(company);
      else this.step = 'company';
    },
    chooseCompany(company) {
      if (!company) return;
      this.selectedCompany = company;
      if (company.pipedriveOrgId) {
        this.resolvedOrgId = company.pipedriveOrgId;
        this.goToForm();
        return;
      }
      this.linkCompany = company;
      this.step = 'link';
      this.linkOpen = true;
    },
    onLinked({ pipedriveOrgId }) {
      this.linked = true;
      this.linkOpen = false;
      this.resolvedOrgId = pipedriveOrgId;
      // Payload: { companyId, pipedriveOrgId } para que el padre refresque la lista.
      this.$emit('company-linked', { companyId: this.linkCompany && this.linkCompany.companyId, pipedriveOrgId });
      this.goToForm();
    },
    onLinkInput(open) {
      this.linkOpen = open;
      // Cerrar el vínculo sin vincular cancela la confirmación completa.
      if (!open && !this.linked && this.step === 'link') this.close();
    },
    async goToForm() {
      this.step = 'form';
      if (!this.nodes) await this.loadOrg();
      this.applyProposedPlacement();
    },
    async loadOrg() {
      this.loadingOrg = true;
      const res = await ClientOrgChartsService.getOrganization(this.resolvedOrgId);
      this.loadingOrg = false;
      if (res.status === 'ok') {
        this.loadedNodes = (res.data && res.data.nodes) || [];
        this.loadedUnplaced = (res.data && res.data.unplaced) || [];
        this.loadedAreas = (res.meta && res.meta.areas) || {};
      } else {
        this.loadedNodes = [];
        this.loadedUnplaced = [];
        this.errorText = errorMessage(res, 'No pudimos cargar el organigrama. Puedes confirmar sin ubicarlo.');
      }
    },
    // Precarga el jefe y el área que propone la investigación, si existen en esta empresa.
    applyProposedPlacement() {
      const candidate = this.candidate || {};
      const proposedName = (candidate.proposedManagerName || '').trim().toLowerCase();
      const manager = proposedName
        ? this.managerOptions.find(person => (person.name || '').trim().toLowerCase() === proposedName)
        : null;
      const proposedArea = candidate.proposedArea || '';
      const areaKey = Object.keys(this.orgAreas).find(
        key => key === proposedArea || this.orgAreas[key] === proposedArea
      );
      this.placement = { manager: manager ? manager.personId : null, area: areaKey || null };
    },
    onDialogInput(open) {
      if (!open) this.close();
    },
    close() {
      this.linkOpen = false;
      this.$emit('input', false);
    },
    buildPayload(extra) {
      const payload = { pipedriveOrgId: this.resolvedOrgId, ...extra };
      if (!payload.existingPipedrivePersonId) {
        payload.name = this.form.name;
        ['title', 'email', 'linkedinUrl'].forEach(field => {
          if (this.form[field]) payload[field] = this.form[field];
        });
      }
      const placement = toPlacement(this.placement);
      if (placement) payload.placement = placement;
      return payload;
    },
    async save(extra = {}) {
      const isFirstTry = !extra.existingPipedrivePersonId && !extra.skipDuplicateCheck;
      if (isFirstTry && this.$refs.form && !this.$refs.form.validate()) return;
      this.saving = true;
      this.errorText = '';
      const res = await ClientOrgChartsService.confirmCandidate(this.candidate.id, this.buildPayload(extra));
      this.saving = false;
      if (res.status === 'ok') {
        // Payload: { candidateId, personId, pipedriveOrgId }
        this.$emit('confirmed', {
          candidateId: this.candidate.id,
          personId: res.data && res.data.personId,
          pipedriveOrgId: this.resolvedOrgId
        });
        this.close();
        return;
      }
      this.handleSaveError(res);
    },
    handleSaveError(res) {
      if (res.code === 'POSSIBLE_DUPLICATE_PERSON' && res.details && res.details.matches && res.details.matches.length) {
        this.matches = res.details.matches;
        return;
      }
      if (res.code === 'COMPANY_NOT_LINKED' && this.selectedCompany) {
        this.linked = false;
        this.chooseCompany({ ...this.selectedCompany, pipedriveOrgId: null });
        return;
      }
      if (res.code === 'CANDIDATE_ALREADY_DECIDED') this.$emit('changed');
      this.errorText = errorMessage(res, 'No pudimos confirmar el contacto. Intenta de nuevo.');
    }
  }
};
</script>
